<script setup lang="ts">
import { Gauge, Route, ScanLine, ShieldCheck } from '@lucide/vue'
import { ref } from 'vue'

import { PButton, PInput } from '@/design-system'

import { login, type SessionState } from '../api'

/**
 * Écran de connexion de la démo. Auth email/mot de passe contre le
 * control-plane PreLLM (via notre backend). Variante « commerciale » du thème
 * PreLLM : split-screen pitch + formulaire (le cockpit, lui, a une carte
 * centrée). L'accès à la démo passe par ce login ; le rôle est tranché côté
 * serveur (super_admin → mode riche).
 */

const emit = defineEmits<{ (e: 'authenticated', state: SessionState): void }>()

const email = ref(localStorage.getItem('prellm.demo.email') || '')
const password = ref('')
const loading = ref(false)
const error = ref('')

const VALUE_PROPS = [
    { icon: ScanLine, text: 'Données sensibles tokenisées avant tout envoi' },
    { icon: ShieldCheck, text: 'Tentatives de manipulation bloquées en amont' },
    { icon: Route, text: 'Acheminement multi-fournisseurs, bascule automatique' },
    { icon: Gauge, text: 'Coût, latence et quota mesurés en temps réel' },
]

async function submit(): Promise<void> {
    if (loading.value) return
    error.value = ''
    if (!email.value.trim() || !password.value) {
        error.value = 'Renseigne ton email et ton mot de passe.'
        return
    }
    loading.value = true
    try {
        const state = await login(email.value.trim(), password.value)
        localStorage.setItem('prellm.demo.email', email.value.trim())
        password.value = ''
        emit('authenticated', state)
    } catch (err) {
        error.value = err instanceof Error ? err.message : 'Connexion impossible'
    } finally {
        loading.value = false
    }
}
</script>

<template>
    <div class="login">
        <!-- Panneau pitch (sombre) -->
        <aside class="login__hero">
            <div class="login__brand">
                <span class="login__logo">PreLLM</span>
                <span class="login__eyebrow">Poste de contrôle · démo</span>
            </div>
            <div class="login__pitch">
                <h1 class="login__headline">La gouvernance LLM, montrée en direct.</h1>
                <ul class="login__props">
                    <li v-for="(p, i) in VALUE_PROPS" :key="i" class="login__prop">
                        <component :is="p.icon" :size="18" :stroke-width="2" />
                        <span>{{ p.text }}</span>
                    </li>
                </ul>
            </div>
            <p class="login__foot">Accès réservé — connecte-toi avec ton compte PreLLM.</p>
        </aside>

        <!-- Formulaire -->
        <main class="login__form-wrap">
            <form class="login__form" @submit.prevent="submit">
                <header class="login__form-head">
                    <h2 class="login__title">Connexion</h2>
                    <p class="login__sub">Accède à la démonstration PreLLM.</p>
                </header>

                <PInput
                    v-model="email"
                    label="Adresse e-mail"
                    type="email"
                    placeholder="vous@exemple.com"
                    autocomplete="username"
                />
                <PInput
                    v-model="password"
                    label="Mot de passe"
                    type="password"
                    placeholder="••••••••"
                    autocomplete="current-password"
                />

                <p v-if="error" class="login__error">{{ error }}</p>

                <PButton variant="primary" size="lg" type="submit" :loading="loading">
                    Se connecter
                </PButton>
            </form>
        </main>
    </div>
</template>

<style scoped>
.login {
    min-height: 100vh;
    display: grid;
    grid-template-columns: 1.1fr 1fr;
    background: var(--p-color-bg);
}
@media (max-width: 860px) {
    .login {
        grid-template-columns: 1fr;
    }
    .login__hero {
        display: none;
    }
}

/* === Hero sombre === */
.login__hero {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 48px;
    padding: 56px 56px 48px;
    background: var(--p-color-ink);
    color: var(--p-color-surface);
}
.login__brand {
    display: flex;
    flex-direction: column;
    gap: 6px;
}
.login__logo {
    font-family: var(--p-font-logo, 'Changa One', cursive);
    font-style: italic;
    font-size: var(--p-text-3xl);
    line-height: 1;
    color: var(--p-color-surface);
}
.login__eyebrow {
    font-family: var(--p-font-mono);
    font-size: var(--p-text-2xs);
    text-transform: uppercase;
    letter-spacing: var(--p-tracking-wider);
    color: var(--p-color-ink-6);
}
.login__pitch {
    display: flex;
    flex-direction: column;
    gap: 28px;
}
.login__headline {
    margin: 0;
    font-family: var(--p-font-display);
    font-size: var(--p-text-2xl);
    font-weight: var(--p-font-weight-medium);
    line-height: var(--p-line-snug);
    max-width: 18ch;
    color: var(--p-color-surface);
}
.login__props {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 16px;
}
.login__prop {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: var(--p-text-sm);
    color: var(--p-color-ink-6);
}
.login__prop :deep(svg) {
    color: var(--p-color-olive-soft);
    flex-shrink: 0;
}
.login__foot {
    margin: 0;
    font-size: var(--p-text-xs);
    color: var(--p-color-ink-5);
}

/* === Formulaire === */
.login__form-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px 28px;
}
.login__form {
    width: 100%;
    max-width: 360px;
    display: flex;
    flex-direction: column;
    gap: 18px;
}
.login__form-head {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 6px;
}
.login__form :deep(.p-button) {
    width: 100%;
    margin-top: 4px;
}
.login__form :deep(.p-input) {
    width: 100%;
}
.login__title {
    margin: 0;
    font-family: var(--p-font-display);
    font-size: var(--p-text-xl);
    font-weight: var(--p-font-weight-medium);
    color: var(--p-color-ink);
}
.login__sub {
    margin: 0;
    font-size: var(--p-text-sm);
    color: var(--p-color-ink-4);
}
.login__error {
    margin: 0;
    padding: 8px 12px;
    border-radius: var(--p-radius-sm);
    background: var(--p-color-brick-bg);
    border: 1px solid var(--p-color-brick-soft);
    color: var(--p-color-brick);
    font-size: var(--p-text-xs);
    line-height: var(--p-line-normal);
}
</style>
